import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { IonIcon } from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import dsPictogram from "../../assets/images/ds-pictogram.svg";
import {
  initialsAvatarDataUri,
  resolveUserAvatarSrc,
} from "../../services/functions";
import "./PushBanner.css";

const VISIBLE_MS = 5000;

/**
 * Foreground banner for personal pushes (new comment, @mention, follow request).
 *
 * Replaces a bare Ionic toast that rendered only "title — body" and an "APRI"
 * button, so an incoming notification gave no visual clue who it was from. This
 * shows the actor's avatar with the same fallback chain the rest of the app
 * uses: real photo → predefined avatar asset → the actor's initials → the Dieta
 * Social pictogram.
 *
 * Editorial / rich pushes still use the central popup; this is only the
 * discreet top banner (is_without_popup).
 */
export default function PushBanner() {
  const history = useHistory();
  const [banner, setBanner] = useState(null);
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    clearTimeout(timerRef.current);
    setBanner(null);
  }, []);

  useEffect(() => {
    const onBanner = (event) => {
      const detail = event?.detail || {};
      if (!detail.title && !detail.body) return;
      setBanner(detail);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setBanner(null), VISIBLE_MS);
    };

    window.addEventListener("push:banner", onBanner);
    return () => {
      window.removeEventListener("push:banner", onBanner);
      clearTimeout(timerRef.current);
    };
  }, []);

  if (!banner) return null;

  const { title, body, path, avatar, actorName } = banner;

  // resolveUserAvatarSrc maps predefined avatar keys/URLs to their bundled asset
  // and treats the backend's placeholder.svg as "no photo", so a user without an
  // avatar falls through to their initials rather than an anonymous grey glyph.
  const avatarSrc =
    resolveUserAvatarSrc(avatar) ||
    initialsAvatarDataUri(actorName || title) ||
    dsPictogram;

  const open = () => {
    dismiss();
    if (path) history.push(path);
  };

  return (
    <div className="push-banner" role="status" aria-live="polite">
      <button type="button" className="push-banner__main" onClick={open}>
        <img
          className="push-banner__avatar"
          src={avatarSrc}
          alt=""
          onError={(e) => {
            e.currentTarget.src = dsPictogram;
          }}
        />
        <span className="push-banner__text">
          {title && <span className="push-banner__title">{title}</span>}
          {body && <span className="push-banner__body">{body}</span>}
        </span>
        {path && <span className="push-banner__cta">Apri</span>}
      </button>
      <button
        type="button"
        className="push-banner__close"
        onClick={dismiss}
        aria-label="Chiudi la notifica"
      >
        <IonIcon icon={closeOutline} />
      </button>
    </div>
  );
}
